import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  DashboardPage,
  DictionariesFuelPriceIndexTab,
  DictionaryACTypesTab,
  DictionarySapSymbolsTab,
  DictionaryFuelTypesTab,
  DictionaryCurrenciesTab,
  DictionaryUnitConverterTab,
  DictionaryParametersTab,
  DictionariesPapIndexTab,
  DictionaryMessageTypesFuelAppTab,
} from '../components/pages';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';
import { AccessForbiddenComponent } from './shared/access-forbidden/access-forbidden.component';
import { RoleGuard } from './shared/guards/role.guard';
import { UserRole } from './shared/guards/userRole';
import { DictionariesFuelFeesTab } from 'src/components/pages/dictionaries-fuelfees-tab/dictionaries-fuelfees-tab';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    component: MsalRedirectComponent,
    pathMatch: 'full',
  },
  {
    path: 'access-forbidden',
    component: AccessForbiddenComponent,
  },
  {
    path: 'dashboard',
    canActivate: [MsalGuard],
    component: DashboardPage,
  },
  {
    path: 'fuel-data',
    canActivate: [MsalGuard],
    loadChildren: () =>
      import('./fuel-data/fuel-data.module').then((m) => m.FuelDataModule),
  },
  {
    path: 'data-errors',
    canActivate: [MsalGuard, RoleGuard],
    data: {
      breadcrumb: null,
      allowedRoles: [UserRole.NewFcaAdmin, UserRole.NewFcaAnalysisTeam],
    },
    loadChildren: () =>
      import('./data-errors/data-errors.module').then(
        (m) => m.DataErrorsModule,
      ),
  },
  {
    path: 'fuel-contracts',
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [
        UserRole.NewFcaAdmin,
        UserRole.NewFcaAnalysisTeam,
        UserRole.NewFcaManagementTeam,
        UserRole.NewFcaSettlementTeam,
      ],
    },
    loadChildren: () =>
      import('./fuel-contracts/fuel-contracts.module').then(
        (m) => m.FuelContractsModule,
      ),
  },
  {
    path: 'sap-export',
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin, UserRole.NewFcaSettlementTeam],
    },
    loadChildren: () =>
      import('./sap-export/sap-export.module').then((m) => m.SapExportModule),
  },
  {
    path: 'volume-calculator',
    canActivate: [MsalGuard, RoleGuard],
    loadChildren: () =>
      import('./volume-calculator/volume-calculator.module').then(
        (m) => m.VolumeCalculatorModule,
      ),
  },
  {
    path: 'dictionary',
    redirectTo: 'dictionary/actypes',
  },
  {
    path: 'dictionary/actypes',
    component: DictionaryACTypesTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/fueltypes',
    component: DictionaryFuelTypesTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/parameters',
    component: DictionaryParametersTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/fuel-fees',
    component: DictionariesFuelFeesTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/fuel-price-index',
    component: DictionariesFuelPriceIndexTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/sap-symbols',
    component: DictionarySapSymbolsTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/currencies',
    component: DictionaryCurrenciesTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/unit-converter',
    component: DictionaryUnitConverterTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/pap-index',
    component: DictionariesPapIndexTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'dictionary/message-types-fuel-app',
    component: DictionaryMessageTypesFuelAppTab,
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
  },
  {
    path: 'reports',
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [
        UserRole.NewFcaAdmin,
        UserRole.NewFcaAnalysisTeam,
        UserRole.NewFcaManagementTeam,
      ],
    },
    loadChildren: () =>
      import('./reports/reports.module').then(
        (m) => m.ReportsModule
      ),
  },
  {
    path: 'fuel-tenders',
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [
        UserRole.NewFcaAdmin,
        UserRole.NewFcaSettlementTeam,
        UserRole.NewFcaManagementTeam,
      ],
    },
    loadChildren: () =>
      import('./fuel-tenders/fuel-tenders.module').then(
        (m) => m.FuelTendersModule,
      ),
  },
  {
    path: 'fuel-app',
    canActivate: [MsalGuard, RoleGuard],
    data: {
      allowedRoles: [UserRole.NewFcaAdmin],
    },
    loadChildren: () =>
      import('./fuel-app/fuel-app.module').then((m) => m.FuelAppModule),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
